import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { InterviewRecordAPI } from '../services/interviewRecordApi';

// Styled Components - 与项目整体Dashboard风格对齐
const Page = styled.div`
  min-height: 100vh;
  padding: 28px 24px;
  background: #f6f7fb;

  @media (max-width: 768px) {
    padding: 18px 14px;
  }
`;

const PageInner = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
`;

const PageSubtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid rgba(17, 24, 39, 0.12);
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.18s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.45);
    box-shadow: 0 8px 18px rgba(17, 24, 39, 0.08);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const CardBody = styled.div`
  padding: 18px 18px;

  @media (max-width: 768px) {
    padding: 14px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.02));
  border: 1px solid rgba(99, 102, 241, 0.18);
  border-radius: 14px;
  padding: 14px 14px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.12);
  color: #111827;
  font-size: 14px;
  font-weight: 800;
`;

const SectionBody = styled.div`
  margin-top: 10px;
  padding: 14px 12px;
  border-radius: 12px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  background: #ffffff;
`;

const Paragraph = styled.p`
  margin: 0;
  color: #374151;
  line-height: 1.75;
  font-size: 14px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ListItem = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #374151;
  line-height: 1.7;
  font-size: 14px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    margin-top: 7px;
    background: #6366f1;
    flex: 0 0 auto;
  }
`;

const NumberedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NumberedItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #374151;
  line-height: 1.7;
  font-size: 14px;
`;

const Num = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #4f46e5;
  flex: 0 0 auto;
  margin-top: 2px;
`;

// eslint-disable-next-line no-unused-vars
const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #dc2626;
  gap: 16px;
`;

const InterviewReportPage = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);

        // 优先从后端获取面试记录
        let record = null;
        try {
          const res = await InterviewRecordAPI.getInterviewRecordRemote(interviewId);
          if (res.success) record = res.data;
        } catch { }
        if (!record) {
          const records = InterviewRecordAPI.getInterviewRecords();
          record = records.find(r => r.id === parseInt(interviewId));
        }

        if (!record) {
          throw new Error('面试记录不存在');
        }

        // 如果有评估数据，直接使用（兼容后端snake_case）
        const evaluation = record.evaluationData || record.evaluation_data;
        if (evaluation) {
          setReportData({
            ...record,
            ...evaluation
          });
        } else {
          // 否则使用模拟数据（实际项目中应该调用API重新评估）
          const mockEvaluationData = {
            score: record.score || 65,
            level: record.score >= 90 ? '优秀' : record.score >= 75 ? '良好' : record.score >= 60 ? '一般' : '需提升',
            overall_evaluation: '候选人具备一定的产品经验和数据驱动意识，尤其在推动淘宝直播后台产品从0到1的过程中展示了初步的项目管理和协调能力。然而，在回答过程中语言表达不够清晰，逻辑结构不强，未能完整阐述推动项目落地的具体方法和遇到的挑战，导致问题解决能力和沟通表达能力评分偏低。此外，面对追问时表现紧张，应变能力较弱，整体表现尚不足以充分展示其胜任高级产品经理岗位的能力。',
            strengths: [
              '具备从0到1建设产品经验（如淘宝直播后台）',
              '有一定的数据驱动意识（如提升用户留存率35%）',
              '具备跨部门协作和团队管理的初步能力'
            ],
            weaknesses: [
              '语言表达不清晰，逻辑性弱，未能有效传达项目细节',
              '回答问题缺乏结构化思维，未能围绕STAR模型展开描述',
              '面对追问反应较慢，应变能力和自信心不足',
              '未能深入阐述推动项目过程中的关键节点和挑战'
            ],
            recommendations: [
              '加强结构化表达训练，使用STAR法则（情境、任务、行动、结果）清晰描述项目经验',
              '准备常见产品面试问题的标准答案模板，提升语言流畅度和逻辑性',
              '模拟压力面试场景，提升临场应变能力和表达自信',
              '深入复盘过往项目，提炼关键成果与挑战，增强问题回答的深度和说服力'
            ]
          };

          setReportData({
            ...record,
            ...mockEvaluationData
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [interviewId]);



  const handleBack = () => {
    navigate('/app/interviews/mock');
  };

  if (loading) {
    return (
      <Page>
        <PageInner>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </PageInner>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <PageInner>
          <ErrorContainer>
            <div>加载报告失败: {error}</div>
            <BackButton onClick={handleBack}>
              <ArrowLeft size={16} />
              返回
            </BackButton>
          </ErrorContainer>
        </PageInner>
      </Page>
    );
  }

  if (!reportData) {
    return (
      <Page>
        <PageInner>
          <ErrorContainer>
            <div>未找到面试报告数据</div>
            <BackButton onClick={handleBack}>
              <ArrowLeft size={16} />
              返回
            </BackButton>
          </ErrorContainer>
        </PageInner>
      </Page>
    );
  }

  return (
    <Page>
      <PageInner>
        <TopBar>
          <TitleGroup>
            <PageTitle>面试评估报告</PageTitle>
            <PageSubtitle>查看本次模拟面试的综合评分与改进建议</PageSubtitle>
          </TitleGroup>

          <BackButton onClick={handleBack}>
            <ArrowLeft size={14} />
            返回
          </BackButton>
        </TopBar>

        <Card>
          <CardBody>
            <Grid>
              <StatGrid>
                <StatCard>
                  <StatValue>{reportData.score}</StatValue>
                  <StatLabel>综合得分</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{reportData.level}</StatValue>
                  <StatLabel>评定等级</StatLabel>
                </StatCard>
              </StatGrid>

              <Section>
                <SectionTitle>总体评价</SectionTitle>
                <SectionBody>
                  <Paragraph>{reportData.overall_evaluation}</Paragraph>
                </SectionBody>
              </Section>

              <Section>
                <SectionTitle>优势亮点</SectionTitle>
                <SectionBody>
                  <List>
                    {reportData.strengths?.map((strength, index) => (
                      <ListItem key={index}>{strength}</ListItem>
                    ))}
                  </List>
                </SectionBody>
              </Section>

              <Section>
                <SectionTitle>待改进项</SectionTitle>
                <SectionBody>
                  <List>
                    {reportData.weaknesses?.map((weakness, index) => (
                      <ListItem key={index}>{weakness}</ListItem>
                    ))}
                  </List>
                </SectionBody>
              </Section>

              <Section>
                <SectionTitle>提升建议</SectionTitle>
                <SectionBody>
                  <NumberedList>
                    {reportData.recommendations?.map((recommendation, index) => (
                      <NumberedItem key={index}>
                        <Num>{index + 1}</Num>
                        <div>{recommendation}</div>
                      </NumberedItem>
                    ))}
                  </NumberedList>
                </SectionBody>
              </Section>
            </Grid>
          </CardBody>
        </Card>
      </PageInner>
    </Page>
  );
};

export default InterviewReportPage;
